import { TextEditor, Icon, Input, Notification, Typography } from '@smartgift/frontend-toolkit';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { DigitalWrapperModal, UploadLogoModal } from '@hero/components/modals';
import { configs } from '@hero/configs';
import {
  AUTOMATION_STATUSES,
  MAX_ADDITIONAL_THOUGHTS_LENGTH,
  MAX_SENDER_NAME_LENGTH,
  MAX_SUBJECT_LENGTH,
  NON_EDITABLE_AUTOMATION_STATUS
} from '@hero/constants';
import {
  DetailItem,
  ImagePreview,
  CustomizeMessageModal,
  MessageLibraryModal
} from '@hero/molecules';
import { GiftMessage } from '@hero/organisms';
import { useGetAutomation, useUpdateAutomation } from '@hero/services';
import { getSafeInnerHtml } from '@hero/utils';

import * as S from './style';

export function AutomationReviewExperienceAccordion() {
  const { t } = useTranslation();
  const { code } = useParams();
  const { data: automation, isSuccess: isAutomationSuccess } = useGetAutomation({ code });
  const { mutateAsync: onUpdate, isLoading: isLoadingUpdateAutomation } = useUpdateAutomation();

  const [showMessageLibrary, setShowMessageLibrary] = useState(false);
  const [experienceSettings, setExperienceSettings] = useState({});
  const [isUploadLogoModalOpen, setIsUploadLogoModalOpen] = useState(false);
  const [isUploadDigitalWrapperModalOpen, setIsUploadDigitalWrapperModalOpen] = useState(false);
  const [isOpenCustomizeMessageModal, setOpenCustomizeMessageModal] = useState();

  const logoUrl = experienceSettings?.branding?.logoUrl;
  const giftWrapperUrl = experienceSettings?.branding?.giftWrapperUrl;

  const { customizedRecipientMessages } = automation?.experienceSettings?.messageParams || {};
  const customMessageLength = Object.keys(customizedRecipientMessages || {}).filter(
    key => !!customizedRecipientMessages[key]?.giftMessage
  ).length;

  const isDetailItemEditable = useMemo(
    () => !NON_EDITABLE_AUTOMATION_STATUS.includes(automation?.status),
    [automation]
  );

  const isActive = useMemo(() => automation?.status === AUTOMATION_STATUSES.active, [automation]);

  function SenderNameEditInput() {
    return (
      <Input
        value={experienceSettings?.messageParams?.senderName}
        inputProps={{
          maxLength: MAX_SENDER_NAME_LENGTH,
          onChange: e => {
            setExperienceSettings({
              ...experienceSettings,
              messageParams: {
                ...experienceSettings.messageParams,
                senderName: e.target.value
              }
            });
          }
        }}
        formItem={{
          placeholder: t('enterASenderName'),
          label: t('senderName'),
          name: 'senderName',
          validations: {
            required: {
              required: true,
              message: t('inputEmptyWarning', { name: t('senderName') })
            }
          }
        }}
      />
    );
  }

  function AdditionalThoughtsEditInput() {
    return (
      isAutomationSuccess && (
        <TextEditor
          key="additional-thoughts-editor"
          placeholder={t('additionalThoughtsLabel')}
          value={experienceSettings?.messageParams?.aboutMessage}
          onChange={e => {
            setExperienceSettings({
              ...experienceSettings,
              messageParams: {
                ...experienceSettings.messageParams,
                aboutMessage: e
              }
            });
          }}
          limit={MAX_ADDITIONAL_THOUGHTS_LENGTH}
        />
      )
    );
  }

  function SubjectLineEditInput() {
    return (
      <Input
        value={experienceSettings?.sharingMethod?.params?.subject}
        variant="outlined"
        name={'subject'}
        label={t('subjectLine')}
        inputProps={{ maxLength: MAX_SUBJECT_LENGTH }}
        onChange={e => {
          setExperienceSettings({
            ...experienceSettings,
            sharingMethod: {
              ...experienceSettings?.sharingMethod,
              params: {
                ...experienceSettings?.sharingMethod?.params,
                subject: e.target.value
              }
            }
          });
        }}
      />
    );
  }

  useEffect(() => {
    if (automation?.experienceSettings) {
      setExperienceSettings(automation.experienceSettings);
    }
  }, [automation]);

  return (
    <S.Experience>
      {isActive && (
        <Notification
          status="info"
          message={<Trans i18nKey="updatingAutomationExperienceInfo" components={{ b: <b /> }} />}
        />
      )}

      <GiftMessage
        setShowMessageLibrary={setShowMessageLibrary}
        setExperienceSettings={setExperienceSettings}
        experienceSettings={experienceSettings}
        associationData={automation}
        updateAssociation={onUpdate}
        isDetailItemEditable={isDetailItemEditable}
        isSuccessAssociation={isAutomationSuccess}
        isOpenCustomizeMessageModal={isOpenCustomizeMessageModal}
        setOpenCustomizeMessageModal={setOpenCustomizeMessageModal}
        footerProps={{
          editable: automation?.status === AUTOMATION_STATUSES.draft,
          customizeActive: automation?.contacts?.ids?.length,
          tagActive: automation?.contacts?.ids?.length
        }}
        customMessageLength={customMessageLength}
        isReviewMessageBadgeShown={customMessageLength > 0 && automation?.contacts?.ids?.length > 0}
        renderCustomizeMessageModa={({
          setOpenCustomizeMessageModal,
          isOpenCustomizeMessageModal
        }) => {
          return (
            <CustomizeMessageModal
              isOpen={isOpenCustomizeMessageModal}
              setIsOpen={setOpenCustomizeMessageModal}
              selectedContacts={automation?.contacts?.ids}
              defaultMessage={experienceSettings?.messageParams?.message}
              customizedRecipientMessages={customizedRecipientMessages}
              updateCustomizedRecipientMessages={customizeMessages => {
                const newExperienceSettings = {
                  ...experienceSettings,
                  messageParams: {
                    ...experienceSettings.messageParams,
                    customizedRecipientMessages: customizeMessages
                  }
                };

                setExperienceSettings(newExperienceSettings);

                onUpdate({
                  params: { code },
                  body: {
                    experienceSettings: newExperienceSettings
                  }
                });
              }}
            />
          );
        }}
      />

      <DetailItem
        title={`${t('logo')}:`}
        withBorder
        editable={isDetailItemEditable}
        onClickEditButton={() => setIsUploadLogoModalOpen(true)}
        editButton={() => <Icon name="edit" />}
      >
        <S.DigitalWrapperContainer>
          {logoUrl && <ImagePreview src={logoUrl} width={80} hasBorder={false} />}
        </S.DigitalWrapperContainer>

        <UploadLogoModal
          isOpen={isUploadLogoModalOpen}
          setIsOpen={setIsUploadLogoModalOpen}
          cropping
          uploadAction={({ cdnUrl }) =>
            onUpdate({
              params: {
                code
              },
              body: {
                experienceSettings: {
                  branding: {
                    ...experienceSettings.branding,
                    logoUrl: cdnUrl
                  }
                }
              }
            })
          }
        />
      </DetailItem>

      <DetailItem
        title={`${t('emailSubjectLine')}:`}
        editable={isDetailItemEditable}
        withBorder
        isCTADisabled={!experienceSettings?.sharingMethod?.params?.subject}
        bodyStyle={{
          display: 'flex',
          alignItems: 'center'
        }}
        closeWithoutSave={() => {
          setExperienceSettings({
            ...experienceSettings
          });
        }}
        saveDetail={() => {
          onUpdate({
            params: { code },
            body: {
              experienceSettings: {
                sharingMethod: {
                  type: 'email',
                  params: {
                    subject: experienceSettings?.sharingMethod?.params?.subject
                  }
                }
              }
            }
          });
        }}
        editInput={SubjectLineEditInput}
      >
        <Typography>{experienceSettings?.sharingMethod?.params?.subject}</Typography>
      </DetailItem>

      <DetailItem
        title={`${t('senderName')}:`}
        withBorder
        bodyStyle={{
          display: 'flex',
          alignItems: 'center'
        }}
        isCTADisabled={!experienceSettings?.messageParams?.senderName}
        editable={isDetailItemEditable}
        closeWithoutSave={() => {
          setExperienceSettings({
            ...experienceSettings,
            messageParams: {
              ...experienceSettings.messageParams,
              senderName: automation?.experienceSettings?.messageParams?.senderName
            }
          });
        }}
        saveDetail={() => {
          onUpdate({
            params: { code },
            body: {
              experienceSettings: {
                messageParams: {
                  senderName: experienceSettings.messageParams.senderName
                }
              }
            }
          });
        }}
        editInput={SenderNameEditInput}
      >
        <Typography as="div" variant="body1">
          {experienceSettings?.messageParams?.senderName}
        </Typography>
      </DetailItem>
      {configs?.advanceSettings?.digitalWrapperEnabled && (
        <DetailItem
          title={`${t('digitalWrapper')}:`}
          editable={isDetailItemEditable}
          withBorder
          onClickEditButton={() => setIsUploadDigitalWrapperModalOpen(true)}
          editButton={() => <Icon name="edit" px={14} />}
        >
          <S.DigitalWrapperContainer>
            {giftWrapperUrl && (
              <ImagePreview
                src={giftWrapperUrl}
                width={80}
                hasBorder={false}
                isWrapper={true}
                hasPreview
              />
            )}
          </S.DigitalWrapperContainer>

          <DigitalWrapperModal
            branding={experienceSettings?.branding}
            isOpen={isUploadDigitalWrapperModalOpen}
            setIsOpen={setIsUploadDigitalWrapperModalOpen}
            isUpdating={isLoadingUpdateAutomation}
            defaultOccasion={automation?.type}
            onUpdateData={({ branding }) => {
              onUpdate({
                params: {
                  code
                },
                body: {
                  experienceSettings: {
                    branding: {
                      ...experienceSettings.branding,
                      ...branding
                    }
                  }
                }
              });
            }}
          />
        </DetailItem>
      )}

      <DetailItem
        title={`${t('collectionDescription')}:`}
        actionAreaStyle={{
          top: '142px',
          bottom: 'auto'
        }}
        isCTADisabled={!experienceSettings?.messageParams?.aboutMessage}
        editable={isDetailItemEditable}
        closeWithoutSave={() => {
          setExperienceSettings({
            ...experienceSettings,
            messageParams: {
              ...experienceSettings?.messageParams,
              aboutMessage: automation?.experienceSettings?.messageParams?.aboutMessage
            }
          });
        }}
        saveDetail={() => {
          onUpdate({
            params: { code },
            body: {
              experienceSettings: {
                messageParams: {
                  aboutMessage: experienceSettings.messageParams.aboutMessage
                }
              }
            }
          });
        }}
        editInput={AdditionalThoughtsEditInput}
      >
        <S.OverviewWrapper>
          <Typography
            color="fg"
            variant="body1"
            {...getSafeInnerHtml(experienceSettings?.messageParams?.aboutMessage)}
          />
        </S.OverviewWrapper>
      </DetailItem>

      {automation?.contacts?.ids?.length > 0 && (
        <CustomizeMessageModal
          editable={automation?.status === AUTOMATION_STATUSES.draft}
          isOpen={isOpenCustomizeMessageModal}
          setIsOpen={setOpenCustomizeMessageModal}
          selectedContacts={automation?.contacts?.ids}
          defaultMessage={automation?.experienceSettings?.messageParams?.message}
          customizedRecipientMessages={customizedRecipientMessages}
          updateCustomizedRecipientMessages={customizeMessages => {
            const newExperienceSettings = {
              ...experienceSettings,
              messageParams: {
                ...experienceSettings?.messageParams,
                customizedRecipientMessages: customizeMessages
              }
            };

            setExperienceSettings(newExperienceSettings);

            onUpdate({
              params: { code },
              body: {
                experienceSettings: newExperienceSettings
              }
            });
          }}
        />
      )}

      {showMessageLibrary && (
        <MessageLibraryModal
          isOpen={showMessageLibrary}
          onClose={() => setShowMessageLibrary(false)}
        />
      )}
    </S.Experience>
  );
}
